/**
 * Auto-apply group-pricing thresholds.
 *
 * When a customer buys enough tickets of a configured department, the backend
 * discount (TIERED discount, already configured in admin) drops the per-ticket
 * price to the "group" rate. Below that threshold we show an upsell nudge —
 * e.g. at 8 of 10 we tell them "Buy 2 more tickets to unlock group pricing".
 *
 * Once the qualifying quantity is reached the nudge disappears and the existing
 * discount messaging (CartRow.getDiscountNode) takes over.
 *
 * NOTE: confirm/replace the department IDs and group prices below against the
 * real departments configured in admin.
 */
export interface GroupPricingTier {
    /** Department the threshold applies to. */
    departmentId: number;
    /** Human label for reference only. */
    label: string;
    /** Tickets of this department required to unlock the group rate. */
    qualifyingQuantity: number;
    /** Per-ticket group price, in cents (e.g. $12.00 -> 1200). */
    groupPriceCents: number;
    /** Start nudging this many tickets before the threshold (2 -> show at 8/10). */
    upsellLeadCount: number;
}

export const GROUP_PRICING_TIERS: GroupPricingTier[] = [
    {
        departmentId: 34228,
        label: "Admission",
        qualifyingQuantity: 10,
        groupPriceCents: 1200,
        upsellLeadCount: 2,
    },
    {
        departmentId: 34229,
        label: "Superpass",
        qualifyingQuantity: 10,
        groupPriceCents: 4500,
        upsellLeadCount: 2,
    },
];

export const getGroupPricingTier = (
    departmentId: number,
): GroupPricingTier | undefined =>
    GROUP_PRICING_TIERS.find((t) => t.departmentId === departmentId);

export interface GroupPricingUpsell {
    /** Tickets still needed to reach the group rate. */
    remaining: number;
    /** Per-ticket group price, in cents. */
    groupPriceCents: number;
}

/**
 * Returns upsell details while the quantity sits inside the lead window
 * (e.g. 8 or 9 of 10), or null otherwise — including once the qualifying
 * quantity is reached, so the existing discount messaging can take over.
 */
export const getGroupPricingUpsell = (
    departmentId: number,
    quantity: number,
): GroupPricingUpsell | null => {
    const tier = getGroupPricingTier(departmentId);
    if (!tier) return null;

    const nudgeFrom = tier.qualifyingQuantity - tier.upsellLeadCount;
    if (quantity >= nudgeFrom && quantity < tier.qualifyingQuantity) {
        return {
            remaining: tier.qualifyingQuantity - quantity,
            groupPriceCents: tier.groupPriceCents,
        };
    }
    return null;
};
